import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  FitText, splitVerseNumber,
  OrnateGoldBadge, GoldLabel, CentralDivider, GoldFooterPill, DivineBackdrop,
  BODY_FONT_STYLE, GOLD_NUM_STYLE,
} from './shared';

const HymnDisplay = ({ content }) => {
  const currentVerse = content?.current_verse || 0;
  const verses = content?.verses || [];
  const language = content?.language || 'english';
  const englishVerses = content?.english_verses;
  const yorubaVerses = content?.yoruba_verses;
  const isBoth = language === 'both' && englishVerses && yorubaVerses;

  const engRaw = isBoth ? (englishVerses[currentVerse] || '') : '';
  const yorRaw = isBoth ? (yorubaVerses[currentVerse] || '') : '';
  const singleRaw = !isBoth ? (verses[currentVerse] || 'No verse content') : '';

  const eng = useMemo(() => splitVerseNumber(engRaw), [engRaw]);
  const yor = useMemo(() => splitVerseNumber(yorRaw), [yorRaw]);
  const single = useMemo(() => splitVerseNumber(singleRaw), [singleRaw]);

  const headerH = '11vh';
  const footerH = '7vh';
  const contentH = `calc(100vh - ${headerH} - ${footerH})`;

  return (
    <div className="w-full" style={{ height: '100vh', position: 'relative', overflow: 'hidden' }} data-testid="hymn-display">
      <DivineBackdrop />

      <div style={{ position: 'relative', zIndex: 10, height: headerH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <OrnateGoldBadge>HYMN {content?.hymn_number}</OrnateGoldBadge>
        </motion.div>
      </div>

      <div style={{ position: 'relative', zIndex: 10, height: contentH, padding: '0 3.5vw' }}>
        {isBoth ? (
          <motion.div
            key={`${currentVerse}-both`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            style={{ display: 'flex', width: '100%', height: '100%', position: 'relative' }}
            data-testid="hymn-split-screen"
          >
            <CentralDivider />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', paddingRight: '2.5vw' }}>
              <GoldLabel>ENGLISH</GoldLabel>
              <div style={{ flex: 1, minHeight: 0 }}>
                <FitText
                  text={engRaw} maxSize={72} minSize={18}
                  className="text-white whitespace-pre-line" style={BODY_FONT_STYLE}
                  renderInner={() => (<>{eng.number && <span style={GOLD_NUM_STYLE}>{eng.number}</span>}{eng.body}</>)}
                />
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', paddingLeft: '2.5vw' }}>
              <GoldLabel>YORUBA</GoldLabel>
              <div style={{ flex: 1, minHeight: 0 }}>
                <FitText
                  text={yorRaw} maxSize={72} minSize={18}
                  className="text-white whitespace-pre-line" style={BODY_FONT_STYLE}
                  renderInner={() => (<>{yor.number && <span style={GOLD_NUM_STYLE}>{yor.number}</span>}{yor.body}</>)}
                />
              </div>
            </div>
          </motion.div>
        ) : (
          <div style={{ width: '100%', height: '100%', maxWidth: '82rem', margin: '0 auto' }} data-testid="hymn-verse-container">
            <motion.div
              key={currentVerse}
              initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.6 }}
              style={{ width: '100%', height: '100%' }}
            >
              <FitText
                text={singleRaw} maxSize={120} minSize={24}
                className="text-white whitespace-pre-line" style={BODY_FONT_STYLE}
                renderInner={() => (<>{single.number && <span style={GOLD_NUM_STYLE}>{single.number}</span>}{single.body}</>)}
              />
            </motion.div>
          </div>
        )}
      </div>

      <div style={{ position: 'relative', zIndex: 10, height: footerH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <GoldFooterPill>VERSE {currentVerse + 1} OF {Math.max(verses.length, 1)}</GoldFooterPill>
      </div>
    </div>
  );
};

export default HymnDisplay;
